import * as actionTypes from '../actions/actionTypes';
import { updateObject } from '../../utils/updateObject';
import { IAutocompleteOption } from '../../models/iautocompleteOption';
import { Reducer } from 'redux';
import { IEquipmentModel } from '../../models/iequipmentModel';
import { IEquipmentGroup } from '../../models/iequipmentGroup';
import { IArea } from '../../models/iarea';
import { IPlace } from '../../models/iplace';
import { ICompany } from '../../models/icompany';
import { IEquipment } from '../../models/iequipment';
import { IPayload } from '../../models/ipayload';
import { IService, emptyService } from '../../models/iservice';
import { IQuestion } from '../../models/iquestion';
import { IOldAction } from '../../components/ServiceOldActionsPanel/ServiceOldActionsPanel';
import { createQuestionTree } from '../../utils/createQuestionTree';
import { IAction as IActionModel } from '../../models/iaction';
import { IFile } from '../../UI/FileList';
import { ISignature } from '../../containers/Services/CreateService/Checklist/SignatureModal';
import { IEquipmentWithServices, TEquipmentWithServicesEquipmentService } from '../../models/iequipmentWithServices';
import { IServiceQuestion } from '../../models/iserviceQuestion';
import { EQuestionType } from '../../models/equestionType';

export enum EServiceSource {
  app = 'app',
  legacyApp = 'legacy app',
  web = 'web',
  legacyWeb = 'legacy web',
  legacy = 'legacy',
  appQrcode = 'app qrcode',
  appCalendar = 'app calendar',
  appPlaces = 'app places',
  appAllEquipments = 'app allequipments',
  appMap = 'app map',
  none = '',
}

export interface IServiceDraftAction {
  accountables: {
    _id: string,
    name: string,
  }[],
  actionText: string,
  deadline: string,
  files: string[],
  question: string,
  questionGroupName: string,
  questionText: string,
  status: string,
  _id: string,
  problemText: string,
}

export interface IServiceDraftQuestion extends Omit<IServiceQuestion, 'actions'> {
  actions: IServiceDraftAction[],
}

export interface IServiceDraft extends Omit<IService, 'questions'> {
  startedSource: EServiceSource,
  startedAt: Date,
  completedAt: EServiceSource | null,
  completedSource: Date | null,
  lastModifiedSource: EServiceSource,
  lastModifiedAt: Date,
  completed: boolean,
  questions: IServiceDraftQuestion[],
  frequency: {
    _id: string,
    name: string,
  },
  serviceType: {
    _id: string,
    name: string,
  },
  equipmentModel: {
    _id: string,
    name: string,
  }
}

export interface IGroup<T> {
  name: string,
  id: string,
  position: number,
  questionArray: T[],
  score: number,
  totalScore: number,
}

export type IQuestionTreeMap = {
  [key: string]: [groupIndex: number, questionIndex: number],
}

export interface IAction<T> {
  type: string;
  payload: T;
}

export interface ICreateServiceState {
  equipments: IPayload<IEquipment>,
  equipmentsLoading: boolean,
  date: Date,
  selectedEquipment: any,
  selectedEquipmentServiceId: string,
  selectedEquipmentService: TEquipmentWithServicesEquipmentService,
  selectedServiceId: string,
  selectedService: IService,
  selectedEquipmentId: string,
  selectedEquipmentWithServices: IEquipmentWithServices,
  questions: IQuestion[],
  questionsTree: IGroup<IQuestion>[],
  questionsTreeMap: IQuestionTreeMap,
  questionsLoading: boolean,
  answeredQuestions: number,
  totalQuestions: number,
  numberOfQuestionsThatShouldHaveAction: number,
  oldActions: IOldAction[],
  equipmentModels: IEquipmentModel[]
  equipmentModelsLoading: boolean
  groups: IEquipmentGroup[]
  groupsLoading: boolean
  areas: IArea[]
  areasLoading: boolean
  places: IPlace[]
  placesLoading: boolean
  companies: ICompany[]
  companiesLoading: boolean
  files: IFile[],
  equipmentTableFilters: {
    filteredModels: IAutocompleteOption[],
    filteredPlaces: IAutocompleteOption[],
    filteredGroups: IAutocompleteOption[],
    filteredAreas: IAutocompleteOption[],
    filteredCompanies: IAutocompleteOption[],
    filteredStatus: {
      valid: boolean,
      validWithOpenedActions: boolean,
      invalid: {
        inDelay: boolean,
        notDone: boolean,
        hasNoServices: boolean,
        hasActionsOpenedOrOverdue: boolean,
        mixed: boolean,
      },
      maintenance: boolean,
      inactive: boolean
    },
  },
  checklistScore: string,
  signature: ISignature,
  loadingSignature: boolean,
  postServiceLoading: boolean,
  refreshLoading: boolean,
  signatures: {name: string, complement: string, file: ISignature}[],
  createServiceDraftLoading: boolean,
  updateServiceDraftLoading: boolean,
  completeServiceLoading: boolean,
  serviceDraftId: string,
  serviceDrafts: IServiceDraft[],
  serviceDraftsCount: number,
  serviceDraftsLoading: boolean,
  deleteServiceDraftLoading: boolean,
  deleteServideDraftId: string,
  draftDate: string,
  draftDescription: string,
  selectedDraft: IServiceDraft | null,
}

const initialStateselectedEquipmentWithServices: IEquipmentWithServices = {
  _id: '',
  serialNumber: '',
  reference: '',
  area: {
    _id: '',
    name: '',
  },
  active: true, 
  brand: '',
  place: {
    _id: '',
    path: '',
    name: '',
  },
  photo: {
    _id: '',
    key: '',
  },
  equipmentModel: {
    _id: '',
    name: '',
    equipmentServices: [],
  },
  description: '',
  equipmentServicesReplicate: [],
  tenant: '',
  statusGlobal: '',
  site: {
    _id: '',
    name: '',
  },
};

const initialState: ICreateServiceState = {
  equipments: {
    count: 0,
    rows: [],
  },
  equipmentsLoading: false,
  date: new Date(),
  selectedEquipment: {},
  selectedServiceId: '',
  selectedService: emptyService,
  selectedEquipmentServiceId: '',
  selectedEquipmentService: {
    _id: '',
    frequency: {
      _id: '',
      name: '',
    },
    mandatory: false,
    mandatoryActionsForNotComplyQuestions: false,
    serviceType: {
      _id: '',
      name: '',
    },
    enableSignature: true,
    mandatorySignature: false,
  },
  selectedEquipmentId: '',
  selectedEquipmentWithServices: initialStateselectedEquipmentWithServices,
  questions: [],
  questionsTree: [],
  questionsTreeMap: {},
  questionsLoading: false,
  numberOfQuestionsThatShouldHaveAction: 0,
  answeredQuestions: 0,
  totalQuestions: 0,
  oldActions: [],
  equipmentModels: [],
  equipmentModelsLoading: false,
  groups: [],
  groupsLoading: false,
  areas: [],
  areasLoading: false,
  places: [],
  placesLoading: false,
  companies: [],
  companiesLoading: false,
  files: [],
  equipmentTableFilters: {
    filteredModels: [],
    filteredPlaces: [],
    filteredGroups: [],
    filteredAreas: [],
    filteredCompanies: [],
    filteredStatus: {
      valid: false,
      validWithOpenedActions: false,
      invalid: {
        inDelay: false,
        notDone: false,
        hasNoServices: false,
        hasActionsOpenedOrOverdue: false,
        mixed: false
      },
      maintenance: false,
      inactive: false
    },
  },
  checklistScore: '0.00%',
  signature: {
    canvas: "",
    signatureId: null,
  },
  loadingSignature: false,
  postServiceLoading: false,
  refreshLoading: false,
  signatures: [],
  createServiceDraftLoading: false,
  updateServiceDraftLoading: false,
  completeServiceLoading: false,
  serviceDraftId: '',
  serviceDrafts: [],
  serviceDraftsCount: 0,
  serviceDraftsLoading: false,
  deleteServiceDraftLoading: false,
  deleteServideDraftId: '',
  draftDate: '',
  draftDescription: '',
  selectedDraft: null
};

//Equipment Models

const fetchEquipmentModelsStart = (state: ICreateServiceState) => {
  return updateObject(state, { equipmentModelsLoading: true });
}

const fetchEquipmentModelsSuccess = (state: ICreateServiceState, action: IAction<IEquipmentModel[]>) => {
  return updateObject(state, { equipmentModelsLoading: false, equipmentModels: action.payload });
}

const fetchEquipmentModelsFail = (state: ICreateServiceState) => {
  return updateObject(state, { equipmentModelsLoading: false });
}

//Groups

const fetchGroupsStart = (state: ICreateServiceState) => {
  return updateObject(state, { groupsLoading: true });
}

const fetchGroupsSuccess = (state: ICreateServiceState, action: IAction<IEquipmentGroup[]>) => {
  return updateObject(state, { groupsLoading: false, groups: action.payload });
}

const fetchGroupsFail = (state: ICreateServiceState) => {
  return updateObject(state, { groupsLoading: false });
}

// Areas

const fetchAreasStart = (state: ICreateServiceState) => {
  return updateObject(state, { areasLoading: true });
}

const fetchAreasSuccess = (state: ICreateServiceState, action: IAction<IArea[]>) => {
  return updateObject(state, { areasLoading: false, areas: action.payload });
}

const fetchAreasFail = (state: ICreateServiceState) => {
  return updateObject(state, { areasLoading: false });
}

// Places

const fetchPlacesStart = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { placesLoading: true });
}

const fetchPlacesSuccess = (state: ICreateServiceState, action: IAction<IPlace[]>) => {
  return updateObject(state, { placesLoading: false, places: action.payload });
}

const fetchPlacesFail = (state: ICreateServiceState) => {
  return updateObject(state, { placesLoading: false });
}

// Companies

const fetchCompaniesStart = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { companiesLoading: true });
}

const fetchCompaniesSuccess = (state: ICreateServiceState, action: IAction<ICompany[]>) => {
  return updateObject(state, { companiesLoading: false, companies: action.payload });
}

const fetchCompaniesFail = (state: ICreateServiceState) => {
  return updateObject(state, { companiesLoading: false });
}

const setEquipmentTableFilters = (state: ICreateServiceState, action: IAction<ICreateServiceState['equipmentTableFilters']>) => {
  return updateObject(state, { equipmentTableFilters: action.payload });
}

const removeEquipmentModelsFilter = (state: ICreateServiceState, action: IAction<IAutocompleteOption>) => {
  let arr = [...state.equipmentTableFilters.filteredModels];
  const filteredModels = arr.filter(model => model.value !== action.payload.value)
  return updateObject(state, { equipmentTableFilters: { ...state.equipmentTableFilters, filteredModels } });
}

const removeGroupsFilter = (state: ICreateServiceState, action: IAction<IAutocompleteOption>) => {
  let arr = [...state.equipmentTableFilters.filteredGroups];
  const filteredGroups = arr.filter(group => group.value !== action.payload.value)
  return updateObject(state, { equipmentTableFilters: { ...state.equipmentTableFilters, filteredGroups } });
}

const removeAreasFilter = (state: ICreateServiceState, action: IAction<IAutocompleteOption>) => {
  let arr = [...state.equipmentTableFilters.filteredAreas];
  const filteredAreas = arr.filter(area => area.value !== action.payload.value)
  return updateObject(state, { equipmentTableFilters: { ...state.equipmentTableFilters, filteredAreas } });
}

const removeCompaniesFilter = (state: ICreateServiceState, action: IAction<IAutocompleteOption>) => {
  let arr = [...state.equipmentTableFilters.filteredCompanies];
  const filteredCompanies = arr.filter(company => company.value !== action.payload.value)
  return updateObject(state, { equipmentTableFilters: { ...state.equipmentTableFilters, filteredCompanies } });
}

const removePlacesFilter= (state: ICreateServiceState, action: IAction<IAutocompleteOption>) => {
  let arr = [...state.equipmentTableFilters.filteredPlaces];
  const filteredPlaces = arr.filter(place => place.value !== action.payload.value)
  return updateObject(state, { equipmentTableFilters: { ...state.equipmentTableFilters, filteredPlaces } });
}

const filterStatus = (state: ICreateServiceState, action: IAction<ICreateServiceState['equipmentTableFilters']['filteredStatus']>) => {
  return updateObject(state, { equipmentTableFilters: { ...state.equipmentTableFilters, filteredStatus: action.payload } });
}

const clearAllFilters = (state: ICreateServiceState) => {
  return updateObject(state, { equipmentTableFilters : initialState.equipmentTableFilters });
}

const fetchEquipmentsStart = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { equipmentsLoading: true });
}

const fetchEquipmentsSuccess = (state: ICreateServiceState, action: IAction<IPayload<IEquipment>>): ICreateServiceState => {
  return updateObject(state, { equipmentsLoading: false, equipments: action.payload });
}

const fetchEquipmentsFail = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { equipmentsLoading: false });
}

const fetchQuestionsStart = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { questionsLoading: true });
}

const fetchQuestionsSuccess = (state: ICreateServiceState, action: IAction<{questions: IQuestion[], defaultAnswerNA: boolean}>): ICreateServiceState => {

  const { questions, defaultAnswerNA } = action.payload;

  const questionsTree = createQuestionTree(questions, '');

  let totalQuestions: number = 0;
  
  const questionsTreeMap: IQuestionTreeMap = {};
  questionsTree.forEach((group, groupIndex) => {
    group.questionArray.forEach((question, questionIndex) => {
      questionsTreeMap[question._id] = [groupIndex, questionIndex];
      question.oldActions = state.oldActions.filter((oldAction) => question._id === oldAction.question);
      question.actions = [];
      question.files = [];
      question.comment = '';
      question.answer = defaultAnswerNA ? 'NA' : '';
      totalQuestions += 1;
    })
  });

  return updateObject(state, {
    questionsLoading: false,
    questions,
    questionsTree,
    questionsTreeMap,
    totalQuestions,
    answeredQuestions: defaultAnswerNA ? totalQuestions : 0,
    checklistScore: '0.00%',
    numberOfQuestionsThatShouldHaveAction: 0,
  });
}

const fetchQuestionsFail = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { questionsLoading: false });
}

const fetchOldActionsStart = (state: ICreateServiceState) => {
  return updateObject(state, {
    questionsLoading: true,
  })
}

const fetchOldActionsSuccess = (state: ICreateServiceState, action: IAction<IOldAction[]>) => {
  return updateObject(state, {
    oldActions: action.payload,
  })
}

const fetchOldActionsFail = (state: ICreateServiceState) => {
  return updateObject(state, {
    questionsLoading: false,
  })
}

const changeServiceDate = (state: ICreateServiceState, action: IAction<Date>) => {
  return updateObject(state, { date: action.payload });
}

const changeQuestionAnswer = (state: ICreateServiceState, action: IAction<{ questionId: string, answer: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  questionsTree[groupIndex].questionArray[questionIndex].answer = action.payload.answer;

  let checklistScore: number = 0;
  let checklistTotalScore: number = 0;

  let answeredQuestions: number = 0;
  let numberOfQuestionsThatShouldHaveAction: number = 0;
  
  questionsTree.forEach((group) => {
    group.score = 0;
    group.totalScore = 0;
    group.questionArray.forEach((question) => {
      if (question.answer === 'NO') {
        group.totalScore += question.weight;
        if (state.selectedEquipmentService.mandatoryActionsForNotComplyQuestions && question.oldActions?.length === 0 && question.actions.length === 0) {
          numberOfQuestionsThatShouldHaveAction += 1;
        }
      }
      else if (question.answer === 'YES') {
        group.score += question.weight;
        group.totalScore += question.weight;
        
      } 
      if (question.answer !== "") {
        answeredQuestions += 1;
      }
    })
    checklistScore += group.score;
    checklistTotalScore += group.totalScore;
  })

  const total = checklistTotalScore > 0 ? `${(checklistScore/checklistTotalScore * 100).toFixed(2)}%`: '0.00%';

  return updateObject(state, { questionsTree, checklistScore: total, answeredQuestions, numberOfQuestionsThatShouldHaveAction })
}

const changeQuestionComment = (state: ICreateServiceState, action: IAction<{ questionId: string, comment: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  // const questionsTree = [...state.questionsTree];
  const questionsTree = state.questionsTree;
  questionsTree[groupIndex].questionArray[questionIndex].comment = action.payload.comment;
  return updateObject(state, { questionsTree })
}

const addQuestionAction = (state: ICreateServiceState, action: IAction<{ questionId: string, action: IActionModel }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  questionsTree[groupIndex].questionArray[questionIndex].actions.push(action.payload.action);
  let numberOfQuestionsThatShouldHaveAction: number = 0;
  questionsTree.forEach((group) => {
    group.score = 0;
    group.totalScore = 0;
    group.questionArray.forEach((question) => {
      if (question.answer === 'NO') {
        group.totalScore += question.weight;
        if (state.selectedEquipmentService.mandatoryActionsForNotComplyQuestions && question.oldActions?.length === 0 && question.actions.length === 0) {
          numberOfQuestionsThatShouldHaveAction += 1;
        }
      }
    })
  })
  return updateObject(state, { questionsTree, numberOfQuestionsThatShouldHaveAction })
}

const editQuestionAction = (state: ICreateServiceState, action: IAction<{ questionId: string, action: IActionModel, actionId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  const actionIndex = questionsTree[groupIndex].questionArray[questionIndex].actions.findIndex((actionA) => actionA._id === action.payload.actionId);
  questionsTree[groupIndex].questionArray[questionIndex].actions[actionIndex] = action.payload.action;
  return updateObject(state, { questionsTree })
}


const deleteQuestionAction = (state: ICreateServiceState, action: IAction<{ questionId: string, actionId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  const actionIndex = questionsTree[groupIndex].questionArray[questionIndex].actions.findIndex((actionA) => actionA._id === action.payload.actionId);
  questionsTree[groupIndex].questionArray[questionIndex].actions.splice(actionIndex, 1);
  let numberOfQuestionsThatShouldHaveAction: number = 0;
  questionsTree.forEach((group) => {
    group.score = 0;
    group.totalScore = 0;
    group.questionArray.forEach((question) => {
      if (question.answer === 'NO') {
        group.totalScore += question.weight;
        if (state.selectedEquipmentService.mandatoryActionsForNotComplyQuestions && question.oldActions?.length === 0 && question.actions.length === 0) {
          numberOfQuestionsThatShouldHaveAction += 1;
        }
      }
    })
  })
  return updateObject(state, { questionsTree, numberOfQuestionsThatShouldHaveAction })
}

const addQuestionFile = (state: ICreateServiceState, action: IAction<{ questionId: string, files: IFile[] }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  questionsTree[groupIndex].questionArray[questionIndex].files = [
    ...questionsTree[groupIndex].questionArray[questionIndex].files,
    ...action.payload.files,
  ];
  return updateObject(state, { questionsTree })
}

const editQuestionFile = (state: ICreateServiceState, action: IAction<{ questionId: string, file: IFile, fileId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  const fileIndex = questionsTree[groupIndex].questionArray[questionIndex].files.findIndex((fileA) => fileA.id === action.payload.fileId);
  questionsTree[groupIndex].questionArray[questionIndex].files[fileIndex] = {
    ...questionsTree[groupIndex].questionArray[questionIndex].files[fileIndex],
    ...action.payload.file,
  };
  return updateObject(state, { questionsTree })
}

const deleteQuestionFile = (state: ICreateServiceState, action: IAction<{ questionId: string, fileId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  // const questionsTree = state.questionsTree;
  const actionIndex = questionsTree[groupIndex].questionArray[questionIndex].files.findIndex((fileA) => fileA.id === action.payload.fileId);
  questionsTree[groupIndex].questionArray[questionIndex].files.splice(actionIndex, 1);
  return updateObject(state, { questionsTree })
}

const addObservationFile = (state: ICreateServiceState, action: IAction<{ files: IFile[] }>) => {

  // const questionsTree = state.questionsTree;
  const newFiles = [
    ...state.files,
    ...action.payload.files,
  ];
  return updateObject(state, { files: newFiles })
}

const editObservationFile = (state: ICreateServiceState, action: IAction<{ file: IFile, fileId: string }>) => {
  const newFiles = [...state.files];
  const fileIndex = state.files.findIndex((fileA) => fileA.id === action.payload.fileId);
  newFiles[fileIndex] = {
    ...newFiles[fileIndex],
    ...action.payload.file,
  };
  return updateObject(state, { files: newFiles })
}

const deleteObservationFile = (state: ICreateServiceState, action: IAction<{ fileId: string }>) => {
  const newFiles = [...state.files];
  const fileIndex = state.files.findIndex((fileA) => fileA.id === action.payload.fileId);
  newFiles.splice(fileIndex, 1);
  return updateObject(state, { files: newFiles })
}

const addSignatureStart = (state: ICreateServiceState) => {
  return updateObject(state, { loadingSignature: true } )
}

const addSignatureSucess = (state: ICreateServiceState, action: IAction<{signature: ISignature}>) => {
  const newSignatures = [...state.signatures, {
    name: '',
    complement: '',
    file: action.payload.signature,
  }];
  return updateObject(state, { loadingSignature: false, signatures: newSignatures } )
}

const addSignatureFail = (state: ICreateServiceState) => {
  return updateObject(state, { loadingSignature: false } )
}
const clear = (state:ICreateServiceState, action: IAction<boolean>) => {
  return updateObject(state, {
    ...initialState,
    selectedEquipmentId: state.selectedEquipmentId,
    equipmentsLoading: state.equipmentsLoading,
    selectedEquipmentService: state.selectedEquipmentService,
    questionsLoading: state.questionsLoading,
    selectedEquipmentWithServices: state.selectedEquipmentWithServices,
    serviceDraftId: action.payload ? '' : state.serviceDraftId,
  })
}

const selectEquipment = (state: ICreateServiceState, action: IAction<string>): ICreateServiceState => {
  return updateObject(state, { selectedEquipmentId: action.payload })
}

const fetchEquipmentServicesStart = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { equipmentsLoading: true });
}

const fetchEquipmentServicesSuccess = (state: ICreateServiceState, action: IAction<IEquipmentWithServices>): ICreateServiceState => {
  return updateObject(state, { equipmentsLoading: false, selectedEquipmentWithServices: action.payload });
}

const fetchEquipmentServicesFail = (state: ICreateServiceState): ICreateServiceState => {
  return updateObject(state, { equipmentsLoading: false });
}

const selectEquipmentService = (state: ICreateServiceState, action: IAction<TEquipmentWithServicesEquipmentService>) => {
  return updateObject(state, { selectedEquipmentService: action.payload });
}

const postServiceStart = (state: ICreateServiceState) => {
  return updateObject(state, { postServiceLoading: true })
}

const postServiceSuccessFail = (state: ICreateServiceState) => {
  return updateObject(state, { postServiceLoading: false })
}

const answerAllQuestionsNa = (state: ICreateServiceState) => {
  const questionsTree = [...state.questionsTree];

  let answeredQuestions: number = 0;
  
  questionsTree.forEach((group) => {
    group.score = 0;
    group.totalScore = 0;
    group.questionArray.forEach((question) => {
      question.answer = 'NA';
      answeredQuestions += 1;
    })
  })

  return updateObject(state, { questionsTree, answeredQuestions, numberOfQuestionsThatShouldHaveAction: 0 })
}

const clearServiceSignatureAndFile = (state: ICreateServiceState) => {
  return updateObject(state, { signature: initialState.signature, files: [] })
}

const refreshOldActionsStart = (state:ICreateServiceState) => {
  return updateObject(state, { refreshLoading: true });
}

const refreshOldActionsSuccess = (state:ICreateServiceState, action: IAction<{ actionsId: string[], questionId: string }>) => {
  const questionTreeCopy = [...state.questionsTree];
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const oldActionsFiltered = state.questionsTree[groupIndex].questionArray[questionIndex].oldActions.filter((oldAction) => {
    return action.payload.actionsId.includes(oldAction._id);
  });
  questionTreeCopy[groupIndex].questionArray[questionIndex].oldActions = oldActionsFiltered;
  return updateObject(state, { refreshLoading: false, questionsTree: questionTreeCopy });
}

const refreshOldActionsFail = (state:ICreateServiceState) => {
  return updateObject(state, { refreshLoading: false });
}

const addSignatureName = (state: ICreateServiceState, action: IAction<{name: string, index: number}>) => {
  const { name, index } = action.payload;
  const signaturesCopy = [...state.signatures];
  signaturesCopy[index].name = name;
  return updateObject(state, { signatures: signaturesCopy } )
}

const addSignatureComplement = (state: ICreateServiceState, action: IAction<{complement: string, index: number}>) => {
  const { complement, index } = action.payload;
  const signaturesCopy = [...state.signatures];
  signaturesCopy[index].complement = complement;
  return updateObject(state, { signatures: signaturesCopy } )
}

const removeSignature = (state: ICreateServiceState, action: IAction<{index: number}>) => {
  const { index } = action.payload;
  const signaturesCopy = [...state.signatures];
  signaturesCopy.splice(index, 1);
  return updateObject(state, { signatures: signaturesCopy } )
}

const createServiceDraftStart = (state: ICreateServiceState) => {
  return updateObject(state, { createServiceDraftLoading: true });
}

const createServiceDraftSuccess = (state: ICreateServiceState, action: IAction<{serviceDraftId: string, serviceDraft: IServiceDraft}>) => {
  return updateObject(state, { createServiceDraftLoading: false, serviceDraftId: action.payload.serviceDraftId, selectedDraft: action.payload.serviceDraft });
}

const createServiceDraftFail = (state: ICreateServiceState) => {
  return updateObject(state, { createServiceDraftLoading: false });
}

const updateServiceDraftStart = (state: ICreateServiceState) => {
  return updateObject(state, { updateServiceDraftLoading: true });
}

const updateServiceDraftSuccess = (state: ICreateServiceState, action: IAction<IServiceDraft>) => {
  return updateObject(state, { updateServiceDraftLoading: false, selectedDraft: action.payload });
}

const updateServiceDraftFail = (state: ICreateServiceState) => {
  return updateObject(state, { updateServiceDraftLoading: false });
}

const completeServiceStart = (state: ICreateServiceState) => {
  return updateObject(state, { completeServiceLoading: true });
}

const completeServiceSuccess = (state: ICreateServiceState) => {
  return updateObject(state, { completeServiceLoading: false });
}

const completeServiceFail = (state: ICreateServiceState) => {
  return updateObject(state, { completeServiceLoading: false });
}

const fetchServiceDraftsStart = (state: ICreateServiceState) => {
  return updateObject(state, { serviceDraftsLoading: true });
}

const fetchServiceDraftsSuccess = (state: ICreateServiceState, action: IAction<{rows: IServiceDraft[], count: number}>) => {
  return updateObject(state, { serviceDraftsLoading: false, serviceDrafts: action.payload.rows, serviceDraftsCount: action.payload.count });
}

const fetchServiceDraftsFail = (state: ICreateServiceState) => {
  return updateObject(state, { serviceDraftsLoading: false });
}

const loadDraftStart = (state: ICreateServiceState) => {
  return updateObject(state, { questionsLoading: true });
}

const loadDraftSuccess = (state: ICreateServiceState, action: IAction<IServiceDraft>) => {
  let answeredQuestions: number = 0;
  const questionsTree = [...state.questionsTree];
  action.payload.questions.forEach((question) => {
    if (question.type === EQuestionType.question) {
      const [groupIndex, questionIndex] = state.questionsTreeMap[question.questionsId];
      let questionFromTree = questionsTree[groupIndex].questionArray[questionIndex];
      // Keep the template `_id` (which is what `questionsTreeMap` is keyed by). The mobile app
      // saves draft questions with a generated `_id` that differs from `questionsId`; spreading it
      // here would break later `questionsTreeMap[question._id]` lookups (e.g. changeQuestionAnswer).
      const questionUpdated = {...questionFromTree, ...question as any, _id: questionFromTree._id};
      questionsTree[groupIndex].questionArray[questionIndex] = questionUpdated;
      if (question.answer !== "") answeredQuestions += 1;
    }
  })

  return updateObject(state, {
    questionsLoading: false,
    serviceDraftId: action.payload._id,
    questionsTree,
    signatures: action.payload.signatures as any,
    files: action.payload.files,
    answeredQuestions,
    date: new Date(action.payload.date),
    draftDescription: action.payload.description,
    selectedDraft: action.payload,
  });
}

const loadDraftFail = (state: ICreateServiceState) => {
  return updateObject(state, { questionsLoading: false });
}

const deleteDraftStart = (state: ICreateServiceState, action: IAction<string>) => {
  return updateObject(state, { deleteServiceDraftLoading: true, deleteServideDraftId: action.payload });
}

const deleteDraftSuccessFail = (state: ICreateServiceState) => {
  return updateObject(state, { deleteServiceDraftLoading: false, deleteServideDraftId: '' });
}

const setServiceDraftId = (state: ICreateServiceState, action: IAction<string>) => {
  return updateObject(state, { serviceDraftId: action.payload });
}

const createServiceReducer: Reducer<ICreateServiceState, IAction<any>> = (state = initialState, action: IAction<any>) => {
  switch(action.type) {
    case actionTypes.CREATE_SERVICE_FETCH_PLACES_START: return fetchPlacesStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_PLACES_SUCCESS: return fetchPlacesSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_PLACES_FAIL: return fetchPlacesFail(state);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENT_MODEL_START: return fetchEquipmentModelsStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENT_MODEL_SUCCESS: return fetchEquipmentModelsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENT_MODEL_FAIL: return fetchEquipmentModelsFail(state);
    case actionTypes.CREATE_SERVICE_FETCH_GROUPS_START: return fetchGroupsStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_GROUPS_SUCCESS: return fetchGroupsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_GROUPS_FAIL: return fetchGroupsFail(state);
    case actionTypes.CREATE_SERVICE_FETCH_AREAS_START: return fetchAreasStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_AREAS_SUCCESS: return fetchAreasSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_AREAS_FAIL: return fetchAreasFail(state);
    case actionTypes.CREATE_SERVICE_FETCH_COMPANIES_START: return fetchCompaniesStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_COMPANIES_SUCCESS: return fetchCompaniesSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_COMPANIES_FAIL: return fetchCompaniesFail(state);
    case actionTypes.CREATE_SERVICE_SET_EQUIPMENTS_TABLE_FILTERS: return setEquipmentTableFilters(state, action);
    case actionTypes.CREATE_SERVICE_REMOVE_AREAS_FILTER: return removeAreasFilter(state, action);
    case actionTypes.CREATE_SERVICE_REMOVE_COMPANIES_FILTER: return removeCompaniesFilter(state, action);
    case actionTypes.CREATE_SERVICE_REMOVE_EQUIPMENT_GROUP_FILTER: return removeGroupsFilter(state, action);
    case actionTypes.CREATE_SERVICE_REMOVE_EQUIPMENT_MODEL_FILTER: return removeEquipmentModelsFilter(state, action);
    case actionTypes.CREATE_SERVICE_REMOVE_PLACES_FILTER: return removePlacesFilter(state, action);
    case actionTypes.CREATE_SERVICE_FILTER_STATUS: return filterStatus(state, action);
    case actionTypes.CREATE_SERVICE_CLEAR_ALL_FILTERS: return clearAllFilters(state);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENTS_START: return fetchEquipmentsStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENTS_SUCCESS: return fetchEquipmentsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENTS_FAIL: return fetchEquipmentsFail(state);
    case actionTypes.CREATE_SERVICE_FETCH_QUESTIONS_START: return fetchQuestionsStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_QUESTIONS_SUCCESS: return fetchQuestionsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_QUESTIONS_FAIL: return fetchQuestionsFail(state);
    case actionTypes.CREATE_SERVICE_GET_OLD_ACTIONS_START: return fetchOldActionsStart(state);
    case actionTypes.CREATE_SERVICE_GET_OLD_ACTIONS_SUCCESS: return fetchOldActionsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_GET_OLD_ACTIONS_FAIL: return fetchOldActionsFail(state);
    case actionTypes.CREATE_SERVICE_CHANGE_SERVICE_DATE: return changeServiceDate(state, action);
    case actionTypes.CREATE_SERVICE_CHANGE_QUESTION_ANSWER: return changeQuestionAnswer(state, action);
    case actionTypes.CREATE_SERVICE_CHANGE_QUESTION_COMMENT: return changeQuestionComment(state, action);
    case actionTypes.CREATE_SERVICE_ADD_QUESTION_ACTION: return addQuestionAction(state, action);
    case actionTypes.CREATE_SERVICE_EDIT_QUESTION_ACTION: return editQuestionAction(state, action);
    case actionTypes.CREATE_SERVICE_DELETE_QUESTION_ACTION: return deleteQuestionAction(state, action);
    case actionTypes.CREATE_SERVICE_ADD_QUESTION_FILE: return addQuestionFile(state, action);
    case actionTypes.CREATE_SERVICE_EDIT_QUESTION_FILE: return editQuestionFile(state, action);
    case actionTypes.CREATE_SERVICE_DELETE_QUESTION_FILE: return deleteQuestionFile(state, action);
    case actionTypes.CREATE_SERVICE_ADD_FILE: return addObservationFile(state, action);
    case actionTypes.CREATE_SERVICE_EDIT_FILE: return editObservationFile(state, action);
    case actionTypes.CREATE_SERVICE_DELETE_FILE: return deleteObservationFile(state, action);
    case actionTypes.CREATE_SERVICE_ADD_SIGNATURE_START: return addSignatureStart(state);
    case actionTypes.CREATE_SERVICE_ADD_SIGNATURE_SUCCESS: return addSignatureSucess(state, action);
    case actionTypes.CREATE_SERVICE_ADD_SIGNATURE_FAIL: return addSignatureFail(state);
    case actionTypes.CREATE_SERVICE_CLEAR: return clear(state, action);
    case actionTypes.CREATE_SERVICE_SELECT_EQUIPMENT: return selectEquipment(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENT_SERVICES_START: return fetchEquipmentServicesStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENT_SERVICES_SUCCESS: return fetchEquipmentServicesSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_EQUIPMENT_SERVICES_FAIL: return fetchEquipmentServicesFail(state);
    case actionTypes.CREATE_SERVICE_SELECT_EQUIPMENT_SERVICE: return selectEquipmentService(state, action);
    case actionTypes.CREATE_SERVICE_POST_SERVICE_START: return postServiceStart(state);
    case actionTypes.CREATE_SERVICE_POST_SERVICE_SUCCESS: return postServiceSuccessFail(state);
    case actionTypes.CREATE_SERVICE_POST_SERVICE_FAIL: return postServiceSuccessFail(state);
    case actionTypes.CREATE_SERVICE_ANSWER_ALL_QUESTIONS_NA: return answerAllQuestionsNa(state);
    case actionTypes.CREATE_SERVICE_CLEAR_SERVICE_FILES_SIGNATURE: return clearServiceSignatureAndFile(state);
    case actionTypes.CREATE_SERVICE_REFRESH_OLD_ACTIONS_START: return refreshOldActionsStart(state);
    case actionTypes.CREATE_SERVICE_REFRESH_OLD_ACTIONS_SUCCESS: return refreshOldActionsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_REFRESH_OLD_ACTIONS_FAIL: return refreshOldActionsFail(state);
    case actionTypes.CREATE_SERVICE_ADD_SIGNATURE_NAME: return addSignatureName(state, action);
    case actionTypes.CREATE_SERVICE_ADD_SIGNATURE_COMPLEMENT: return addSignatureComplement(state, action);
    case actionTypes.CREATE_SERVICE_REMOVE_SIGNATURE: return removeSignature(state, action);

    //DRAFTS
    case actionTypes.CREATE_SERVICE_DRAFT_START: return createServiceDraftStart(state);
    case actionTypes.CREATE_SERVICE_DRAFT_SUCCESS: return createServiceDraftSuccess(state, action);
    case actionTypes.CREATE_SERVICE_DRAFT_FAIL: return createServiceDraftFail(state);
    case actionTypes.CREATE_SERVICE_UPDATE_DRAFT_START: return updateServiceDraftStart(state);
    case actionTypes.CREATE_SERVICE_UPDATE_DRAFT_SUCCESS: return updateServiceDraftSuccess(state, action);
    case actionTypes.CREATE_SERVICE_UPDATE_DRAFT_FAIL: return updateServiceDraftFail(state);
    case actionTypes.CREATE_SERVICE_COMPLETE_DRAFT_START: return completeServiceStart(state);
    case actionTypes.CREATE_SERVICE_COMPLETE_DRAFT_SUCCESS: return completeServiceSuccess(state);
    case actionTypes.CREATE_SERVICE_COMPLETE_DRAFT_FAIL: return completeServiceFail(state);
    case actionTypes.CREATE_SERVICE_FETCH_SERVICE_DRAFTS_START: return fetchServiceDraftsStart(state);
    case actionTypes.CREATE_SERVICE_FETCH_SERVICE_DRAFTS_SUCCESS: return fetchServiceDraftsSuccess(state, action);
    case actionTypes.CREATE_SERVICE_FETCH_SERVICE_DRAFTS_FAIL: return fetchServiceDraftsFail(state);
    case actionTypes.CREATE_SERVICE_DELETE_DRAFT_START: return deleteDraftStart(state, action);
    case actionTypes.CREATE_SERVICE_DELETE_DRAFT_SUCCESS: return deleteDraftSuccessFail(state);
    case actionTypes.CREATE_SERVICE_DELETE_DRAFT_FAIL: return deleteDraftSuccessFail(state);
    case actionTypes.CREATE_SERVICE_LOAD_DRAFT_START: return loadDraftStart(state);
    case actionTypes.CREATE_SERVICE_LOAD_DRAFT_SUCCESS: return loadDraftSuccess(state, action);
    case actionTypes.CREATE_SERVICE_LOAD_DRAFT_FAIL: return loadDraftFail(state);
    case actionTypes.CREATE_SERVICE_SET_SERVICE_DRAFT_ID: return setServiceDraftId(state, action);
    default: return state;
  }
}

export default createServiceReducer;
